import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
    isExcludeProductSelection,
    isIncludeProductSelection,
    ProductSelection
} from '../../shared/product-selection/ProductSelectionTypes';
import { ACTION_DECREASE, ACTION_INCREASE, ACTION_SET } from '../../store/constants';
import { globalErrorResponse, isErrorOfType } from '../../shared/notification/actions';
import { invalidateCartSummaryQuery } from './useCartSummaryQuery';
import { invalidateShoppingCartProducts } from './useShoppingCartProducts';
import { invalidateCartGroupedItems } from './useCartGroupedItems';
import { ChangeAmountByLineItemKeysRequest } from '../selection-list/decreaseAmountBulkInListByLineItemKeys';
import { convertLineItemKeysToDtoFromProductSelection } from '../../shared/utils/convertLineItemKey';
import { ChangeAmountByFilterRequest } from '../selection-list/decreaseAmountBulkInListByFilter';
import { setAmountBulkInCartByLineItemKeys } from './setAmountBulkInCartByLineItemKeys';
import { increaseAmountBulkInCartByLineItemKeys } from './increaseAmountBulkInCartByLineItemKeys';
import { decreaseAmountBulkInCartByLineItemKeys } from './decreaseAmountInCartByLineItemKeys';
import { setAmountBulkInCartByFilter } from './setAmountBulkInCartByFilter';
import { increaseAmountBulkInCartByFilter } from './increaseAmountBulkInCartByFilter';
import { decreaseAmountBulkInCartByFilter } from './decreaseAmountInCartByFilter';
import { log } from '../../shared/utils/log';
import { invalidateCartAggregations } from '../../shop/cart/useCartAggregations';
import { showNotification } from '../../shared/notification/toastNotification';
import { ProductFilter } from '../../store/api';

type GetRequestParams = {
    amount: number;
    command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE;
    filterRequest: ProductFilter;
    productSelection?: ProductSelection;
};

const changeAmount = ({ amount, command, filterRequest, productSelection }: GetRequestParams, t: TFunction) => {
    if (isIncludeProductSelection(productSelection)) {
        const body: ChangeAmountByLineItemKeysRequest = {
            lineItemKeys: convertLineItemKeysToDtoFromProductSelection(productSelection)
        };
        switch (command) {
            case ACTION_DECREASE:
                return decreaseAmountBulkInCartByLineItemKeys(body);
            case ACTION_INCREASE:
                return increaseAmountBulkInCartByLineItemKeys(body);
            case ACTION_SET:
                return setAmountBulkInCartByLineItemKeys({ ...body, targetAmount: amount });
        }
    } else if (isExcludeProductSelection(productSelection)) {
        const body: ChangeAmountByFilterRequest = {
            filterRequest,
            excludedLineItemKeys: productSelection.excluded
        };
        switch (command) {
            case ACTION_DECREASE:
                return decreaseAmountBulkInCartByFilter(body);
            case ACTION_INCREASE:
                return increaseAmountBulkInCartByFilter(body);
            case ACTION_SET:
                return setAmountBulkInCartByFilter({ ...body, targetAmount: amount });
        }
    } else {
        throw new Error(t('notifications.buildAddProductsCallback.error'));
    }
};

export const useChangeAmountBulkInCart = (productSelection: ProductSelection, productFilter: ProductFilter) => {
    const { t } = useTranslation();

    return {
        changeAmountBulk: async (
            amount: number,
            command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE
        ) => {
            try {
                const response = await changeAmount(
                    {
                        amount,
                        command,
                        filterRequest: productFilter,
                        productSelection
                    },
                    t
                );

                showNotification(t('notifications.copyAmountChangeCartBulk.message'));
                invalidateCartSummaryQuery().then();
                invalidateShoppingCartProducts().then();
                invalidateCartGroupedItems().then();
                invalidateCartAggregations().then();

                return response;
            } catch (error) {
                log.error(error, { causedBy: 'useChangeAmountBulkInCart' });

                if (isErrorOfType(error)) {
                    globalErrorResponse(error);
                }

                throw error;
            }
        }
    };
};
